import { useEffect, useState } from 'react'
import {
  Stack, Text, Group, Badge, Alert, Skeleton, Table, Paper, Tabs, MultiSelect,
  SegmentedControl, Code, Loader, SimpleGrid, Button,
} from '@mantine/core'
import { IconLock, IconAlertTriangle, IconInfoCircle, IconRefresh } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useCan, useCurrentTenant } from '@/store/auth'
import { formatApiError } from '@/api/errors'
import {
  explicarMotivo, lerTrilha, listarBackups, listarClusters,
  type ClusterCloudHsm,
} from '@/api/cloudhsm'
import { ChavesDoHsm, UsuariosDoHsm } from './GestaoDoHsm'
import { useSessaoDoHsm } from './sessaoDoHsm'
import { InicializacaoDoCluster } from './InicializarCluster'
import { VersaoDoExecutor } from './VersaoDoExecutor'
import { AdicionarHsm } from './AdicionarHsm'
import { AtualizarExecutor } from './AtualizarExecutor'

/**
 * Tela "CloudHSM" (Segurança).
 *
 * Leitura: o cluster, os HSMs, os backups e a trilha de auditoria que o próprio HSM escreveu.
 * Gestão (F6): usuários e chaves do HSM, pelo executor na VPC do tenant, com a credencial do
 * HSM digitada na tela e guardada só na memória da aba (`sessaoDoHsm.ts`).
 *
 * Regra que não pode cair: região que não foi lida NÃO é região sem cluster. Com a
 * role sem `cloudhsm:DescribeClusters`, a AWS recusa; dizer "nenhum cluster" aí seria
 * afirmar algo sobre a conta do cliente sem ter olhado.
 */

const REGIOES_SEMPRE_OFERECIDAS = ['us-east-1', 'us-east-2']
const HORAS = ['1', '6', '24', '72', '168']
const LIMITE_DA_TRILHA = 200
// Depois de ativar, a AWS leva alguns segundos para passar o cluster de INITIALIZED a
// ACTIVE: a lista é relida neste intervalo, dentro desta janela, enquanto houver cluster
// INITIALIZED. Sem isso o cartão ficava em INITIALIZED até recarregar a página.
export const INTERVALO_POS_ATIVACAO_MS = 5_000
export const JANELA_POS_ATIVACAO_MS = 60_000

function quando(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('pt-BR')
}

function corDoEstado(estado: string): string {
  if (estado === 'ACTIVE') return 'green'
  if (estado.includes('FAILED') || estado === 'DEGRADED') return 'red'
  if (estado.includes('DELETE')) return 'gray'
  return 'yellow'
}

/** O backend responde 403/409/502 com `detail` conhecido; o resto cai no genérico. */
function erroDaLeitura(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { detail?: unknown } } }
  const status = e?.response?.status
  const detail = e?.response?.data?.detail
  if (status === 403) return 'Você não tem a permissão cloudhsm:view neste tenant.'
  if (status === 409) return 'Este tenant ainda não tem conta AWS associada.'
  if (status === 502 && (detail as { motivo?: string } | undefined)?.motivo === 'tenant_assume_role_failed') {
    return 'Não foi possível assumir a role de leitura na conta deste tenant.'
  }
  return formatApiError(err, 'Não foi possível consultar o CloudHSM.')
}

export function CloudHsmPage() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  // Vazio = as regiões do tenant (o backend decide).
  const [regioes, setRegioes] = useState<string[]>([])
  // Fim da janela de releitura depois de uma ativação (epoch ms); null = sem ativação recente.
  const [releituraAte, setReleituraAte] = useState<number | null>(null)

  const consulta = useQuery({
    queryKey: ['cloudhsm', 'clusters', tenantId, regioes.join(',')],
    queryFn: () => listarClusters(tenantId!, regioes),
    enabled: !!tenantId,
    retry: false,
    staleTime: 60_000,
    refetchInterval: (q) => {
      if (releituraAte === null || Date.now() > releituraAte) return false
      const aindaInicializado = q.state.data?.clusters.some((c) => c.estado === 'INITIALIZED')
      return aindaInicializado ? INTERVALO_POS_ATIVACAO_MS : false
    },
  })

  if (!tenantId) {
    return <Text c="dimmed">Selecione um tenant para ver o CloudHSM.</Text>
  }

  const dados = consulta.data
  const opcoes = [...new Set([...(dados?.regioes_do_tenant ?? []), ...REGIOES_SEMPRE_OFERECIDAS, ...regioes])]
  const falhas = Object.entries(dados?.regioes_com_falha ?? {})
  const lidas = dados?.regioes_lidas ?? []

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Text size="lg" fw={700}>
            <IconLock size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            CloudHSM
          </Text>
          <Text size="sm" c="dimmed">
            Clusters, HSMs, backups e a trilha de auditoria do HSM na conta do tenant, e a gestão dos
            usuários e chaves do HSM.
          </Text>
        </Stack>
        <MultiSelect
          label="Regiões"
          placeholder={regioes.length === 0 ? 'Regiões do tenant' : undefined}
          data={opcoes}
          value={regioes}
          onChange={setRegioes}
          clearable
          w={320}
        />
      </Group>

      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        Usuários e chaves são geridos com o login do próprio HSM, digitado na aba Usuários ou Chaves
        do cluster. A credencial fica só na memória desta aba por 10 minutos; o ConsoleAI não a grava.
      </Alert>

      {consulta.isLoading && <Skeleton h={160} />}

      {consulta.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Não foi possível consultar o CloudHSM">
          {erroDaLeitura(consulta.error)}
        </Alert>
      )}

      {dados && (
        <>
          <Group gap="xs">
            <Text size="sm" c="dimmed">Regiões consultadas:</Text>
            {dados.regioes_consultadas.length === 0 && <Text size="sm" c="dimmed">nenhuma</Text>}
            {dados.regioes_consultadas.map((r) => (
              <Badge key={r} variant="light" color={dados.regioes_com_falha[r] ? 'red' : 'green'}>
                {r}{dados.regioes_com_falha[r] ? ' — não lida' : ''}
              </Badge>
            ))}
          </Group>

          {falhas.map(([regiao, motivo]) => (
            <Alert key={regiao} color="orange" icon={<IconAlertTriangle size={16} />}
              title={`Região ${regiao} não foi lida`}>
              Não deu para ver se há CloudHSM em {regiao}: {explicarMotivo(motivo)}.
            </Alert>
          ))}

          {dados.clusters.length === 0 && lidas.length > 0 && (
            <Text c="dimmed">
              Nenhum cluster de CloudHSM {lidas.length === 1 ? 'na região lida' : 'nas regiões lidas'}: {lidas.join(', ')}.
            </Text>
          )}

          {dados.clusters.map((c) => (
            <CartaoDoCluster key={`${c.regiao}/${c.cluster_id}`} tenantId={tenantId} cluster={c}
              atualizando={consulta.isFetching} onAtualizar={() => { void consulta.refetch() }}
              onAtivado={() => setReleituraAte(Date.now() + JANELA_POS_ATIVACAO_MS)} />
          ))}
        </>
      )}
    </Stack>
  )
}

function Campo({ rotulo, valor }: { rotulo: string; valor: string | null | undefined }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed">{rotulo}</Text>
      <Text size="sm" ff="monospace">{valor || '—'}</Text>
    </Stack>
  )
}

function CartaoDoCluster({ tenantId, cluster, atualizando, onAtualizar, onAtivado }: {
  tenantId: number
  cluster: ClusterCloudHsm
  atualizando: boolean
  onAtualizar: () => void
  onAtivado: () => void
}) {
  const [aba, setAba] = useState<string | null>('hsms')
  const subnets = Object.entries(cluster.subnets)
  const retencao = cluster.retencao_de_backup
  const can = useCan()
  const podeGerir = can('cloudhsm', 'manage')
  const podeApagar = can('cloudhsm', 'delete')
  // As sessões vivem no cartão, e não na aba: trocar de aba não descarta o login no HSM.
  const sessaoAdmin = useSessaoDoHsm()
  const sessaoCripto = useSessaoDoHsm()
  // --- A: inicialização pela tela (#26221) ---
  // Inicializado por esta tela: quando a AWS chegar a INITIALIZED, leva para a aba Usuários, onde
  // está "Ativar cluster".
  const [inicializouAqui, setInicializouAqui] = useState(false)
  useEffect(() => {
    if (inicializouAqui && cluster.estado === 'INITIALIZED') {
      setAba('usuarios')
      setInicializouAqui(false)
    }
  }, [inicializouAqui, cluster.estado])

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <Text fw={700} ff="monospace">{cluster.cluster_id}</Text>
            <Badge color={corDoEstado(cluster.estado)} variant="light">{cluster.estado}</Badge>
            {cluster.modo && (
              <Badge color={cluster.modo === 'FIPS' ? 'blue' : 'gray'} variant="outline">{cluster.modo}</Badge>
            )}
          </Group>
          <Group gap="xs">
            <Text size="sm" c="dimmed">{cluster.regiao}</Text>
            {/* HSM criado fora da tela (apply do Terraform) só aparecia recarregando a página. */}
            <Button size="xs" variant="subtle" leftSection={<IconRefresh size={14} />}
              loading={atualizando} onClick={onAtualizar}>
              Atualizar
            </Button>
          </Group>
        </Group>
        {cluster.mensagem_de_estado && <Text size="sm" c="dimmed">{cluster.mensagem_de_estado}</Text>}

        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          <Campo rotulo="Tipo de HSM" valor={cluster.tipo_de_hsm} />
          <Campo rotulo="VPC" valor={cluster.vpc_id} />
          <Campo rotulo="Security group" valor={cluster.security_group} />
          <Campo rotulo="Criado em" valor={quando(cluster.criado_em)} />
          <Campo rotulo="Subnets" valor={subnets.map(([az, s]) => `${az}: ${s}`).join(', ')} />
          <Campo rotulo="Retenção de backup" valor={retencao ? `${retencao.valor} (${retencao.tipo})` : null} />
        </SimpleGrid>

        {/* --- A: inicialização pela tela (#26221) --- */}
        <InicializacaoDoCluster tenantId={tenantId} cluster={cluster} podeGerir={podeGerir}
          onAtualizar={onAtualizar} onInicializado={() => setInicializouAqui(true)} />

        <Tabs value={aba} onChange={setAba}>
          <Tabs.List>
            <Tabs.Tab value="hsms">HSMs ({cluster.hsms.length})</Tabs.Tab>
            <Tabs.Tab value="backups">Backups</Tabs.Tab>
            <Tabs.Tab value="trilha">Trilha do HSM</Tabs.Tab>
            <Tabs.Tab value="usuarios">Usuários</Tabs.Tab>
            <Tabs.Tab value="chaves">Chaves</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="hsms" pt="sm"><TabelaDeHsms cluster={cluster} /></Tabs.Panel>
          <Tabs.Panel value="backups" pt="sm">
            {aba === 'backups' && <Backups tenantId={tenantId} cluster={cluster} />}
          </Tabs.Panel>
          <Tabs.Panel value="trilha" pt="sm">
            {aba === 'trilha' && <Trilha tenantId={tenantId} cluster={cluster} />}
          </Tabs.Panel>
          <Tabs.Panel value="usuarios" pt="sm">
            {aba === 'usuarios' && (
              <UsuariosDoHsm tenantId={tenantId} cluster={cluster} sessao={sessaoAdmin}
                podeGerir={podeGerir} podeApagar={podeApagar} onAtivado={onAtivado} />
            )}
          </Tabs.Panel>
          <Tabs.Panel value="chaves" pt="sm">
            {aba === 'chaves' && (
              <ChavesDoHsm tenantId={tenantId} cluster={cluster} sessao={sessaoCripto}
                podeGerir={podeGerir} podeApagar={podeApagar} />
            )}
          </Tabs.Panel>
        </Tabs>

        {/* --- D1 versao do executor (#26390) --- */}
        <VersaoDoExecutor tenantId={tenantId} cluster={cluster} />

        {/* --- B-T/D2 (#26388, #26396) --- */}
        <AdicionarHsm tenantId={tenantId} cluster={cluster} podeGerir={podeGerir} />
        <AtualizarExecutor tenantId={tenantId} cluster={cluster} podeGerir={podeGerir} />
      </Stack>
    </Paper>
  )
}

function TabelaDeHsms({ cluster }: { cluster: ClusterCloudHsm }) {
  if (cluster.hsms.length === 0) {
    return <Text size="sm" c="dimmed">Este cluster não tem HSM.</Text>
  }
  return (
    <Table striped withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>HSM</Table.Th><Table.Th>Estado</Table.Th><Table.Th>Zona</Table.Th>
          <Table.Th>Subnet</Table.Th><Table.Th>ENI</Table.Th><Table.Th>IP</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {cluster.hsms.map((h) => (
          <Table.Tr key={h.hsm_id}>
            <Table.Td ff="monospace">{h.hsm_id}</Table.Td>
            <Table.Td>
              <Badge color={corDoEstado(h.estado)} variant="light">{h.estado}</Badge>
              {h.mensagem_de_estado && <Text size="xs" c="dimmed">{h.mensagem_de_estado}</Text>}
            </Table.Td>
            <Table.Td>{h.zona ?? '—'}</Table.Td>
            <Table.Td ff="monospace">{h.subnet_id ?? '—'}</Table.Td>
            <Table.Td ff="monospace">{h.eni_id ?? '—'}</Table.Td>
            <Table.Td ff="monospace">{h.ip ?? '—'}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function Backups({ tenantId, cluster }: { tenantId: number; cluster: ClusterCloudHsm }) {
  const q = useQuery({
    queryKey: ['cloudhsm', 'backups', tenantId, cluster.regiao, cluster.cluster_id],
    queryFn: () => listarBackups(tenantId, cluster.cluster_id, cluster.regiao),
    retry: false,
    staleTime: 60_000,
  })
  if (q.isLoading) return <Loader size="sm" />
  if (q.isError) return <Alert color="red">{erroDaLeitura(q.error)}</Alert>
  const d = q.data!
  if (d.falha) {
    return <Alert color="orange">Os backups não foram lidos: {explicarMotivo(d.falha)}.</Alert>
  }
  if (d.backups.length === 0) return <Text size="sm" c="dimmed">Este cluster não tem backup.</Text>
  return (
    <Table striped withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Backup</Table.Th><Table.Th>Estado</Table.Th><Table.Th>Criado em</Table.Th>
          <Table.Th>Expira</Table.Th><Table.Th>Origem</Table.Th><Table.Th>Apagado em</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {d.backups.map((b) => (
          <Table.Tr key={b.backup_id}>
            <Table.Td ff="monospace">{b.backup_id}</Table.Td>
            <Table.Td><Badge color={corDoEstado(b.estado)} variant="light">{b.estado}</Badge></Table.Td>
            <Table.Td>{quando(b.criado_em)}</Table.Td>
            <Table.Td>{b.nunca_expira ? 'Nunca expira' : 'Segue a retenção'}</Table.Td>
            <Table.Td>
              {b.backup_de_origem
                ? `cópia de ${b.backup_de_origem} (${b.regiao_de_origem ?? '—'}) em ${quando(b.copiado_em)}`
                : '—'}
            </Table.Td>
            <Table.Td>{quando(b.apagado_em)}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function Trilha({ tenantId, cluster }: { tenantId: number; cluster: ClusterCloudHsm }) {
  const [horas, setHoras] = useState('24')
  const q = useQuery({
    queryKey: ['cloudhsm', 'trilha', tenantId, cluster.regiao, cluster.cluster_id, horas],
    queryFn: () => lerTrilha(tenantId, cluster.cluster_id, cluster.regiao, Number(horas), LIMITE_DA_TRILHA),
    retry: false,
    staleTime: 30_000,
  })
  const d = q.data

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        O que o próprio HSM registrou — login, criação de usuário, geração de chave —, feito pelo
        cloudhsm-cli ou por esta tela. A mensagem aparece como o HSM a escreveu.
      </Text>
      <Group gap="xs">
        <Text size="sm">Últimas</Text>
        <SegmentedControl size="xs" data={HORAS.map((h) => ({ value: h, label: `${h} h` }))}
          value={horas} onChange={setHoras} />
      </Group>

      {q.isLoading && <Loader size="sm" />}
      {q.isError && <Alert color="red">{erroDaLeitura(q.error)}</Alert>}
      {d && d.falha && (
        <Alert color="orange">A trilha não foi lida: {explicarMotivo(d.falha)}.</Alert>
      )}
      {d && !d.falha && d.log_group_existe === false && (
        <Text size="sm" c="dimmed">
          O HSM ainda não escreveu trilha (log group <Code>{d.log_group}</Code> não existe).
        </Text>
      )}
      {d && d.truncada && (
        <Alert color="yellow">Há mais eventos no período: mostrando os {d.eventos.length} mais recentes.</Alert>
      )}
      {d && d.janela_incompleta && (
        <Alert color="yellow">
          Janela grande demais, reduza o período: a leitura parou antes do fim e os eventos mostrados
          podem não ser os mais recentes.
        </Alert>
      )}
      {d && !d.falha && d.log_group_existe === true && d.eventos.length === 0 && (
        <Text size="sm" c="dimmed">Nenhum evento nas últimas {d.horas} h.</Text>
      )}
      {d && d.eventos.length > 0 && (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr><Table.Th>Quando</Table.Th><Table.Th>HSM</Table.Th><Table.Th>Mensagem</Table.Th></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {d.eventos.map((e, i) => (
              <Table.Tr key={`${e.quando}-${i}`}>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>{quando(e.quando)}</Table.Td>
                <Table.Td ff="monospace">{e.hsm ?? '—'}</Table.Td>
                <Table.Td>
                  <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{e.mensagem}</Code>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  )
}
